// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.js';
import { apiClient } from '../services/api-client.js';
import { useLocale } from '../hooks/useLocale.js';
import { useInstalledLanguages } from '../hooks/useInstalledLanguages.js';
import { usePageTitle } from '../hooks/usePageTitle.js';
import { safeNextPath } from '../utils/safe-next-path.js';
import { PASSWORD_CHANGE_REQUIRED_ROUTE } from '../utils/password-change-required.js';
import { isMfaCodeShape, normaliseMfaCode } from '../utils/mfa-code.js';
import type { SsoLoginOption } from '@vn-grc/shared';

export function LoginPage() {
  usePageTitle('Login');
  const navigate = useNavigate();
  const { login, providerSessionNotEnded } = useAuth();
  const { t, locale, setLocale } = useLocale();
  const { languages } = useInstalledLanguages();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [pendingUser, setPendingUser] = useState<any>(null);
  const [searchParams] = useSearchParams();

  /*
   * Sprint 63.6.0 — the federated sign-in control is offered ONLY when the
   * tenant is named in the URL (AC B2).
   *
   * There is no other way to know which organisation is at the keyboard: this
   * page has no session, and asking the server "does an organisation with this
   * id have SSO?" for every id somebody types would turn `/public-info` into an
   * organisation oracle. The link with `?org=` in it is what an administrator
   * sends their people, and in its absence the page is exactly what it was
   * before this sprint — which is AC A1.
   */
  const orgHint = searchParams.get('org');
  /*
   * Sprint 63.6.3 (#338) — EVERY usable configuration, in the order the server
   * declared. An organisation in the middle of a provider migration holds two,
   * and the previous shape could describe only one: the server picked the first
   * in stored order and nothing on screen said a choice had been made. The page
   * decides nothing here; it renders the list as the server sent it, marks no
   * option as preferred or required (the public options carry no such flag, by
   * the owner's ruling: which provider an organisation requires is not told to
   * somebody who has not signed in), and hands each option's protocol to the URL
   * builder, which is the one place that knows the route shape (Sprint 63.6.1).
   */
  const [ssoOptions, setSsoOptions] = useState<SsoLoginOption[]>([]);
  const [checkingSso, setCheckingSso] = useState(false);

  useEffect(() => {
    if (!orgHint) {
      setSsoOptions([]);
      return;
    }
    let cancelled = false;
    setCheckingSso(true);
    apiClient
      .getSsoPublicInfo(orgHint)
      .then((info) => {
        if (cancelled) return;
        /*
         * No option, no control. `has_sso` and `options` are read together: a
         * `has_sso: true` with an empty list (or the pre-63.6.3 single-config
         * shape, which has no list) offers nothing rather than a control that
         * leads nowhere. The list is rendered in the order the server declared;
         * nothing is marked or re-sorted here, because the response does not say
         * which option the organisation enforces.
         */
        const offered = info?.has_sso && Array.isArray(info.options) ? info.options : [];
        setSsoOptions(offered);
      })
      .catch(() => {
        /*
         * A failure here removes the SSO control and leaves password sign-in
         * working. It is deliberately silent: the person at this page has a
         * password field in front of them and nothing to do about an
         * availability problem on a lookup they did not ask for.
         */
        if (!cancelled) setSsoOptions([]);
      })
      .finally(() => {
        if (!cancelled) setCheckingSso(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orgHint]);

  /** The error the ACS redirect leaves behind when a federated login fails. */
  const ssoError = searchParams.get('sso_error') ? t.login.sso_failed : '';

  /*
   * Sprint 63.23.0 — the OAuth consent page sends a signed-out visitor here
   * with `?next=/oauth/consent?request=…` and must get them back. Only a
   * same-origin path is honoured (`safeNextPath`); anything else lands on
   * `/admin` as before, so this is not an open redirect.
   */
  const safeNext = safeNextPath(searchParams.get('next'));
  const afterLogin = safeNext ?? '/admin';
  // Gate E12.5 pass 1 (P2-7): the enrolment interstitial carries the same
  // `next`, so a person who signed in to approve an app returns to it after
  // enrolling instead of landing on /admin.
  const enrolmentPath = safeNext
    ? `/mfa-enrolment-required?next=${encodeURIComponent(safeNext)}`
    : '/mfa-enrolment-required';

  const normalisedMfaCode = normaliseMfaCode(mfaCode);
  const mfaCodePlausible = isMfaCodeShape(normalisedMfaCode);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (mfaRequired && !mfaCodePlausible) return;
    setError('');
    setLoading(true);
    try {
      // If MFA step is active, verify the TOTP or backup code
      if (mfaRequired && pendingUser) {
        await apiClient.verifyMfa({ code: normalisedMfaCode });
        login(pendingUser);
        // Sprint 63.29.0 (#541) — the code was the first hold; a required
        // password change is the second, and the server answers in that order.
        navigate(
          pendingUser.password_change_required ? PASSWORD_CHANGE_REQUIRED_ROUTE : afterLogin,
        );
        return;
      }

      // All login flows go through the backend (which sets httpOnly cookie)
      const result = (await apiClient.login({ email, password })) as {
        token: string;
        refresh_token: string;
        user: Record<string, unknown>;
        mfa_required?: boolean;
        mfa_enrolment_required?: boolean;
        password_change_required?: boolean;
      };

      // If MFA is required, show the code input
      if (result.mfa_required) {
        setPendingUser({
          ...result.user,
          password_change_required: result.password_change_required,
        });
        setMfaRequired(true);
        return;
      }

      /*
       * Sprint 63.7.0 — the organisation requires a second factor and this
       * account has none.
       *
       * Checked AFTER `mfa_required`, because the two cannot both be true (a
       * user with a secret is challenged; a user without one may be held for
       * enrolment) and the challenge is the narrower remedy if they somehow
       * were.
       *
       * The user IS logged in — the session exists and the cookies are set.
       * What the session cannot do is reach anything but the six enrolment
       * paths, so it is `login()` followed by a redirect to the interstitial
       * rather than an error: telling somebody their password was wrong when it
       * was right is the failure mode this whole series keeps finding.
       */
      login({
        ...result.user,
        mfa_enrolment_required: result.mfa_enrolment_required,
        password_change_required: result.password_change_required,
      });
      if (result.mfa_enrolment_required) {
        navigate(enrolmentPath);
        return;
      }
      /*
       * Sprint 63.29.0 (#541) — an administrator requires a new password. The
       * session exists and reaches only the change-password step, so this is a
       * redirect, never an error: the password the person typed was right.
       */
      if (result.password_change_required) {
        navigate(PASSWORD_CHANGE_REQUIRED_ROUTE);
        return;
      }

      navigate(afterLogin);
    } catch (err: any) {
      // Branch on the SEMANTIC code, not the message. `err.response` is always
      // undefined (ApiError exposes `serverCode`), and `err.message` is the
      // backend's prose — "Account is locked until …" — never the code. So both
      // equality checks below were unreachable and a locked-out user fell
      // through to `invalid_credentials`: the UI reported a locked account as a
      // wrong password and sent the user into a retry loop that cannot succeed.
      const serverCode = (err as { serverCode?: string })?.serverCode;
      if (serverCode === 'MFA_ENROLMENT_REQUIRED') {
        /*
         * Sprint 63.7.0 — reached when a session that was ALREADY held tries to
         * sign in again, or when an in-flight request races the escalation.
         * The remedy is the interstitial, not a retype of the password.
         */
        navigate(enrolmentPath);
        return;
      }
      if (serverCode === 'SSO_LOGIN_REQUIRED') {
        // Sprint 63.6.0 — this organisation has opted in to SSO-only, and this
        // person is not exempt. Reporting it as a wrong password would send
        // them into a retry loop that cannot succeed.
        setError(t.login.sso_required);
      } else if (serverCode === 'ACCOUNT_LOCKED') {
        setError(t.login.account_locked);
      } else if (serverCode === 'ACCOUNT_DEACTIVATED') {
        setError(t.login.account_deactivated);
      } else if (serverCode === 'max_concurrent_sessions_reached') {
        setError(t.login.session_limit_reached);
      } else if (serverCode === 'csrf_failed') {
        setError(t.login.session_expired);
      } else if (serverCode === 'MFA_VERIFICATION_LOCKED') {
        // Not `invalid_mfa`: while locked, a correct code is refused too.
        setError(t.login.mfa_step_up_locked);
      } else if (err?.code === 'FORBIDDEN' || err?.status === 403) {
        setError(t.login.login_failed);
      } else if (mfaRequired) {
        setError(t.login.invalid_mfa);
      } else {
        setError(t.login.invalid_credentials);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <img
        src="/logo_compliance.png"
        alt="AesirX"
        className="login-logo"
        style={{ height: 100, width: 'auto' }}
      />
      <div className="login-container">
        <h1>{t.login.title}</h1>
        <p className="login-subtitle">{t.login.subtitle}</p>
        {providerSessionNotEnded && (
          <div className="dm-alert dm-alert--info" role="status" id="login-provider-session-notice">
            {t.login.sso_provider_session_not_ended}
          </div>
        )}
        {(error || ssoError) && (
          <div className="login-error" role="alert" id="login-error">
            {error || ssoError}
          </div>
        )}
        <form onSubmit={handleSubmit} className="dm-form" aria-label="Login form">
          {!mfaRequired ? (
            <>
              <div className="form-group">
                <label htmlFor="email">{t.login.email}</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="user@example.com"
                  required
                  autoFocus
                  aria-required="true"
                  aria-invalid={error ? true : undefined}
                  aria-describedby={error ? 'login-error' : undefined}
                />
              </div>
              <div className="form-group">
                <label htmlFor="password">{t.login.password}</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  aria-required="true"
                  aria-invalid={error ? true : undefined}
                  aria-describedby={error ? 'login-error' : undefined}
                />
              </div>
            </>
          ) : (
            <div className="form-group">
              <label htmlFor="mfaCode">{t.login.mfa_code}</label>
              <input
                id="mfaCode"
                type="text"
                inputMode="text"
                autoComplete="one-time-code"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                placeholder={t.login.mfa_step_up_placeholder}
                required
                autoFocus
                aria-required="true"
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? 'mfa-code-hint login-error' : 'mfa-code-hint'}
              />
              <small id="mfa-code-hint">{t.login.mfa_step_up_instruction}</small>
            </div>
          )}
          <button
            type="submit"
            className="btn btn-primary login-btn"
            disabled={loading || (mfaRequired && !mfaCodePlausible)}
          >
            {loading
              ? mfaRequired
                ? t.login.verifying
                : t.login.signing_in
              : mfaRequired
                ? t.login.verify
                : t.login.sign_in}
          </button>
        </form>
        {orgHint && !mfaRequired && (
          <div className="login-sso" style={{ marginTop: '1rem', textAlign: 'center' }}>
            {checkingSso ? (
              <p role="status">{t.login.sso_checking}</p>
            ) : ssoOptions.length > 0 ? (
              <>
                <p aria-hidden="true">{t.login.sso_divider}</p>
                <ul
                  className="login-sso-options"
                  role="list"
                  aria-label={t.login.sso_options_label}
                >
                  {ssoOptions.map((option) => (
                    <li key={option.config_id}>
                      {/*
                        The accessible name is built from visible text: the
                        provider's display name and its protocol. Two options therefore never share a name unless the
                        administrator gave two configurations the same name and
                        the same protocol.
                      */}
                      <a
                        className="btn btn-secondary login-btn"
                        href={apiClient.ssoLoginUrl(
                          option.config_id,
                          searchParams.get('next') ?? undefined,
                          option.provider_type,
                        )}
                      >
                        {`${t.login.sso_sign_in_with} ${option.display_name ?? ''}`.trim()}
                        <span className="login-sso-meta">{` (${option.provider_type.toUpperCase()})`}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </div>
        )}
        <div className="login-links" style={{ marginTop: '1rem', textAlign: 'center' }}>
          <a
            href="/forgot-password"
            onClick={(e) => {
              e.preventDefault();
              navigate('/forgot-password');
            }}
          >
            {t.login.forgot_password}
          </a>
        </div>
      </div>
      <div className="login-language-selector">
        <select value={locale} onChange={(e) => setLocale(e.target.value)} aria-label="Language">
          {languages.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
